/**
 * AttorneyOverlayPage — Phase 09b/09f/09g.
 *
 * Editor for the attorney work-product overlay. Reads + writes via:
 *   GET  /api/v1/attorney/overlay/{report_id}    (Phase 09b)
 *   POST /api/v1/attorney/overlay/{report_id}    (Phase 09b)
 *
 * Two states:
 *   - finalized → read-only viewer (legacy 09b behavior)
 *   - draft (or no overlay yet) → full editor with block CRUD
 *
 * Blocks marked FINAL_PDF visibility get composed into the narrative
 * PDF (Phase 09f). INTERNAL_ONLY blocks are attorney work-product that
 * stays off the rendered document.
 *
 * Auth: Clerk session (attorneys authenticate via Clerk only — the legacy
 * attorney JWT was retired).
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  FileText,
  FileEdit,
  HelpCircle,
  Loader2,
  Lock,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import type { components } from '../types/generated/api';
import { BlockType, BlockVisibility } from '../types/generated/api';
import { getAuthHeaders } from '../lib/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Types — sourced from backend Pydantic via openapi-typescript codegen.
// Renaming any field on the backend regenerates `api.ts` and breaks the
// `_PIN` constants below at compile time.
// ---------------------------------------------------------------------------

type AttorneyOverlay = components['schemas']['AttorneyOverlay'];
type ContentBlock = components['schemas']['ContentBlock'];

const BLOCK_TYPES: ReadonlyArray<{ value: BlockType; label: string }> = [
  { value: BlockType.narrative_section, label: 'Narrative Section' },
  { value: BlockType.exhibit_selection, label: 'Exhibit Selection' },
  { value: BlockType.strategy_note, label: 'Strategy Note (internal)' },
  { value: BlockType.cover_letter_paragraph, label: 'Cover Letter Paragraph' },
  { value: BlockType.annotation, label: 'Annotation (anchored)' },
] as const;

// Compile-time field pins — renaming any backend field on AttorneyOverlay
// or ContentBlock breaks tsc HERE with the bad string in the error message.
const _OVERLAY_FIELD_PIN: ReadonlyArray<keyof AttorneyOverlay> = [
  'report_id',
  'version',
  'attested_calc_ref',
  'attorney_id',
  'attorney_name',
  'created_at',
  'updated_at',
  'status',
  'content_blocks',
] as const;
void _OVERLAY_FIELD_PIN;

const _CONTENT_BLOCK_FIELD_PIN: ReadonlyArray<keyof ContentBlock> = [
  'block_id',
  'block_type',
  'position',
  'anchor',
  'content',
  'created_at',
  'last_edited_at',
  'visibility',
] as const;
void _CONTENT_BLOCK_FIELD_PIN;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function newBlock(position: number): ContentBlock {
  const now = new Date().toISOString();
  return {
    block_id: `local-${Math.random().toString(36).slice(2, 10)}`,
    block_type: BlockType.narrative_section,
    position,
    anchor: null,
    content: '',
    created_at: now,
    last_edited_at: now,
    visibility: BlockVisibility.final_pdf,
  };
}

// ---------------------------------------------------------------------------
// Decision-guide tooltip — annotation vs challenge
// ---------------------------------------------------------------------------

function DecisionGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block" data-testid="overlay-decision-guide">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="cs-meta inline-flex items-center gap-1"
        style={{ color: 'var(--color-needs-human-text)' }}
      >
        <HelpCircle className="w-3.5 h-3.5" />
        Annotation vs. challenge?
      </button>
      {open && (
        <div
          data-testid="overlay-decision-guide-content"
          className="cs-meta absolute z-10 left-0 top-full mt-2 w-96 rounded-lg shadow-lg p-4 space-y-2"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
        >
          <p>
            <strong style={{ color: 'var(--color-needs-human-text)' }}>Annotation</strong> — your
            commentary or framing on top of the existing engine output.
            Doesn't change any number; doesn't trigger re-deliberation.
            Use when you want to add legal context, exhibit citations, or
            cover-letter paragraphs alongside the attested calculation.
          </p>
          <p>
            <strong style={{ color: 'var(--color-needs-human-text)' }}>Challenge</strong> — you
            believe the engine's output is wrong and want a re-deliberation.
            Triggers a War Room run and may invalidate downstream engines.
            Limited to 2 per engine per report. Use the war-room page's
            <em> File a Challenge</em> button instead of an annotation.
          </p>
          <p className="italic" style={{ color: 'var(--color-text-muted)' }}>
            Rule of thumb: if you'd be willing to defend the engine's number
            in court but want to add context → annotate. If you wouldn't →
            challenge.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block editor row
// ---------------------------------------------------------------------------

function BlockEditor({
  block,
  index,
  total,
  onChange,
  onDelete,
  onMove,
}: {
  block: ContentBlock;
  index: number;
  total: number;
  onChange: (updated: ContentBlock) => void;
  onDelete: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  return (
    <li
      className="p-4 space-y-3"
      style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}
      data-testid={`overlay-block-editor-${block.block_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid={`block-type-${block.block_id}`}
            value={block.block_type}
            onChange={(e) =>
              onChange({ ...block, block_type: e.target.value as BlockType })
            }
            className="cs-meta rounded px-2 py-1"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            {BLOCK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            data-testid={`block-visibility-${block.block_id}`}
            value={block.visibility}
            onChange={(e) =>
              onChange({
                ...block,
                visibility: e.target.value as BlockVisibility,
              })
            }
            className="cs-meta rounded px-2 py-1"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            <option value="final_pdf">Final PDF</option>
            <option value="internal_only">Internal Only</option>
          </select>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            className="p-1 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ color: 'var(--color-text-muted)' }}
            title="Move up"
            data-testid={`block-move-up-${block.block_id}`}
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            className="p-1 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ color: 'var(--color-text-muted)' }}
            title="Move down"
            data-testid={`block-move-down-${block.block_id}`}
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-1"
            style={{ color: 'var(--color-brand)' }}
            title="Delete block"
            data-testid={`block-delete-${block.block_id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {block.block_type === 'annotation' && (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="text"
            placeholder="engine_id (e.g. fair_settlement)"
            value={block.anchor?.engine_id || ''}
            onChange={(e) =>
              onChange({
                ...block,
                anchor: { ...(block.anchor || {}), engine_id: e.target.value },
              })
            }
            className="cs-meta rounded px-2 py-1"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            data-testid={`block-anchor-engine-${block.block_id}`}
          />
          <input
            type="text"
            placeholder="field_id (optional)"
            value={block.anchor?.field_id || ''}
            onChange={(e) =>
              onChange({
                ...block,
                anchor: { ...(block.anchor || {}), field_id: e.target.value },
              })
            }
            className="cs-meta rounded px-2 py-1"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            data-testid={`block-anchor-field-${block.block_id}`}
          />
        </div>
      )}

      <textarea
        data-testid={`block-content-${block.block_id}`}
        rows={4}
        value={block.content}
        onChange={(e) =>
          onChange({
            ...block,
            content: e.target.value,
            last_edited_at: new Date().toISOString(),
          })
        }
        placeholder="Block content…"
        className="cs-body w-full rounded-md px-3 py-2"
        style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Read-only block row (for finalized overlays)
// ---------------------------------------------------------------------------

function BlockRow({ block }: { block: ContentBlock }) {
  return (
    <li
      className="p-4"
      style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}
      data-testid={`overlay-block-${block.block_id}`}
    >
      <div className="flex items-start justify-between mb-2 gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="cs-label">
            {block.block_type.replace(/_/g, ' ')}
          </span>
          {block.visibility === 'internal_only' && (
            <span
              className="cs-chip px-1.5 py-0.5 rounded"
              style={{ color: 'var(--color-text-muted)', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
            >
              internal only
            </span>
          )}
          {block.anchor && (block.anchor.engine_id || block.anchor.field_id) && (
            <span className="cs-figure" style={{ color: 'var(--color-text-muted)' }}>
              ↳ {block.anchor.engine_id || ''}
              {block.anchor.field_id ? `.${block.anchor.field_id}` : ''}
            </span>
          )}
        </div>
        <span className="cs-meta flex-shrink-0" style={{ color: 'var(--color-text-dim)' }}>
          edited {formatDate(block.last_edited_at)}
        </span>
      </div>
      <p className="cs-body whitespace-pre-wrap" style={{ color: 'var(--color-text-secondary)' }}>{block.content}</p>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AttorneyOverlayPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [overlay, setOverlay] = useState<AttorneyOverlay | null>(null);
  const [editingBlocks, setEditingBlocks] = useState<ContentBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emptyState, setEmptyState] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [attestedCalcRef, setAttestedCalcRef] = useState<string>('');

  const isFinalized = overlay?.status === 'finalized';
  const isEditable = !isFinalized;

  useEffect(() => {
    if (!reportId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setEmptyState(false);
      try {
        const authHeaders = await getAuthHeaders();
        if (!authHeaders.Authorization) {
          navigate('/attorney/login');
          return;
        }
        const res = await fetch(
          `${BACKEND_URL}/api/v1/attorney/overlay/${reportId}`,
          { headers: authHeaders },
        );
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) {
          navigate('/attorney/login');
          return;
        }
        if (res.status === 404) {
          setEmptyState(true);
          setOverlay(null);
          setEditingBlocks([]);
          return;
        }
        if (!res.ok) {
          setError(`Failed to load overlay (HTTP ${res.status})`);
          return;
        }
        const body: AttorneyOverlay = await res.json();
        if (!cancelled) {
          setOverlay(body);
          setEditingBlocks(
            [...body.content_blocks].sort((a, b) => a.position - b.position),
          );
          setAttestedCalcRef(body.attested_calc_ref || '');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load overlay');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId, navigate]);

  // ----- block CRUD handlers ------------------------------------------------

  function handleAddBlock() {
    setSaveSuccess(null);
    setEditingBlocks((blocks) => [...blocks, newBlock(blocks.length)]);
  }

  function handleChangeBlock(index: number, updated: ContentBlock) {
    setSaveSuccess(null);
    setEditingBlocks((blocks) =>
      blocks.map((b, i) => (i === index ? updated : b)),
    );
  }

  function handleDeleteBlock(index: number) {
    setSaveSuccess(null);
    setEditingBlocks((blocks) =>
      blocks
        .filter((_, i) => i !== index)
        .map((b, i) => ({ ...b, position: i })),
    );
  }

  function handleMoveBlock(index: number, direction: -1 | 1) {
    setSaveSuccess(null);
    setEditingBlocks((blocks) => {
      const target = index + direction;
      if (target < 0 || target >= blocks.length) return blocks;
      const next = [...blocks];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((b, i) => ({ ...b, position: i }));
    });
  }

  // ----- save / finalize ----------------------------------------------------

  async function persist(finalize: boolean) {
    if (!reportId) return;
    setSaveError(null);
    setSaveSuccess(null);
    if (!attestedCalcRef.trim()) {
      setSaveError('Calc run ID is required before saving.');
      return;
    }
    if (editingBlocks.some((b) => !b.content.trim())) {
      setSaveError('Every block must have content. Delete empty blocks before saving.');
      return;
    }
    setSaving(true);
    try {
      const authHeaders = await getAuthHeaders();
      const url = `${BACKEND_URL}/api/v1/attorney/overlay/${reportId}${finalize ? '?finalize=true' : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attested_calc_ref: attestedCalcRef,
          content_blocks: editingBlocks.map((b, i) => ({
            block_id: b.block_id.startsWith('local-') ? undefined : b.block_id,
            block_type: b.block_type,
            position: i,
            anchor: b.anchor || undefined,
            content: b.content,
            visibility: b.visibility,
          })),
        }),
      });
      if (res.status === 401 || res.status === 403) {
        navigate('/attorney/login');
        return;
      }
      if (res.status === 409) {
        setSaveError(
          'This version is finalized and locked. Re-attest the report to start a new draft version.',
        );
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setSaveError(body?.detail || `Failed to save (HTTP ${res.status})`);
        return;
      }
      const body: AttorneyOverlay = await res.json();
      setOverlay(body);
      setEditingBlocks(
        [...body.content_blocks].sort((a, b) => a.position - b.position),
      );
      setEmptyState(false);
      setSaveSuccess(
        finalize
          ? `Finalized v${body.version}. The overlay is now locked.`
          : `Saved v${body.version} draft.`,
      );
    } catch (e: any) {
      setSaveError(e?.message || 'Network error');
    } finally {
      setSaving(false);
    }
  }

  if (!reportId) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
        <p className="cs-meta">No report ID provided.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }} data-testid="attorney-overlay-page">
      <div className="px-6 py-4" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="cs-meta flex items-center gap-2 mb-3"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="cs-display">
                Attorney Overlay
              </h1>
              <p className="cs-meta mt-1">
                Work-product overlay for report{' '}
                <code className="cs-figure px-1.5 py-0.5 rounded" style={{ background: 'var(--color-bg-elevated)' }}>
                  {reportId}
                </code>
              </p>
            </div>
            <DecisionGuide />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {loading && (
          <div
            className="cs-body p-8 text-center"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, color: 'var(--color-text-muted)' }}
            data-testid="attorney-overlay-loading"
          >
            Loading overlay…
          </div>
        )}

        {error && !loading && (
          <div
            className="cs-body p-4 flex items-start gap-3"
            style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14, color: 'var(--color-needs-human-text)' }}
            data-testid="attorney-overlay-error"
          >
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
            <div>{error}</div>
          </div>
        )}

        {(emptyState || (overlay && isEditable)) && !loading && !error && (
          <div className="space-y-6">
            {/* Calc run ref input — required to save */}
            {emptyState && (
              <div
                className="p-6"
                style={{ background: 'var(--color-bg-surface)', border: '2px dashed var(--color-border)', borderRadius: 14 }}
                data-testid="attorney-overlay-empty"
              >
                <FileText className="w-8 h-8 mb-2" style={{ color: 'var(--color-text-ghost)' }} />
                <h2 className="cs-h2">
                  Start a new overlay
                </h2>
                <p className="cs-meta mt-1 mb-4">
                  No overlay exists for this report yet. Add at least one
                  content block and save to create v1.
                </p>
                <label className="cs-label block mb-1">
                  Attested calc run ID
                </label>
                <input
                  data-testid="overlay-calc-ref-input"
                  type="text"
                  value={attestedCalcRef}
                  onChange={(e) => setAttestedCalcRef(e.target.value)}
                  placeholder="The engine_outputs run_id this overlay pairs with"
                  className="cs-body w-full rounded-md px-3 py-2"
                  style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
                />
              </div>
            )}

            {/* Existing overlay metadata (when editing draft of v1+) */}
            {overlay && isEditable && (
              <div
                className="p-4"
                style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}
                data-testid="attorney-overlay-meta"
              >
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="cs-h3">
                      Version {overlay.version} (draft)
                    </h2>
                    <p className="cs-meta mt-0.5">
                      by {overlay.attorney_name} · last updated{' '}
                      {formatDate(overlay.updated_at)}
                    </p>
                  </div>
                  <span
                    className="cs-chip px-2 py-1 rounded flex items-center gap-1"
                    style={{ color: 'var(--color-needs-human-text)', background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
                    data-testid="attorney-overlay-status-draft"
                  >
                    <FileEdit className="w-3 h-3" />
                    draft
                  </span>
                </div>
              </div>
            )}

            {/* Phase 09f banner */}
            <div
              data-testid="attorney-overlay-pdf-inclusion"
              className="cs-meta p-4 flex items-start gap-3"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 14, color: 'var(--color-text-secondary)' }}
            >
              <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
              <div>
                <p className="mb-1" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>PDF inclusion</p>
                <p>
                  When this report's narrative PDF is regenerated, blocks
                  marked <strong>final pdf</strong> will be composed into a
                  dedicated <em>ATTORNEY WORK PRODUCT</em> section near the
                  end of the document. Blocks marked <strong>internal only</strong> stay
                  off the rendered PDF.
                </p>
                <p className="mt-2" data-testid="attorney-overlay-attestation-note">
                  If the report has been signed off by the in-house
                  forensic accountant, an <strong>ATTESTATION</strong> block
                  is also rendered above the overlay section, capturing the
                  accountant's name, sign-off date, and a court-admissible
                  attestation paragraph.
                </p>
              </div>
            </div>

            {/* Block editor list */}
            <div data-testid="attorney-overlay-blocks">
              <h3 className="cs-h3 mb-3">
                Content blocks ({editingBlocks.length})
              </h3>
              {editingBlocks.length === 0 ? (
                <p
                  className="cs-meta italic"
                  data-testid="attorney-overlay-blocks-empty"
                >
                  No blocks yet. Click "Add block" to start.
                </p>
              ) : (
                <ul className="space-y-3">
                  {editingBlocks.map((b, i) => (
                    <BlockEditor
                      key={b.block_id}
                      block={b}
                      index={i}
                      total={editingBlocks.length}
                      onChange={(updated) => handleChangeBlock(i, updated)}
                      onDelete={() => handleDeleteBlock(i)}
                      onMove={(dir) => handleMoveBlock(i, dir)}
                    />
                  ))}
                </ul>
              )}

              <button
                type="button"
                onClick={handleAddBlock}
                data-testid="overlay-add-block"
                className="cs-body mt-3 inline-flex items-center gap-1"
                style={{ fontWeight: 500, color: 'var(--color-needs-human-text)' }}
              >
                <Plus className="w-4 h-4" />
                Add block
              </button>
            </div>

            {/* Save / finalize bar */}
            <div
              className="p-4 flex items-center justify-between gap-3 flex-wrap"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}
            >
              <div className="cs-meta">
                Save Draft to keep editing. Finalize to lock this version
                (v
                {overlay ? overlay.version : 1}
                ).
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => persist(false)}
                  disabled={saving || editingBlocks.length === 0}
                  data-testid="overlay-save-draft"
                  className="cs-body inline-flex items-center gap-1 px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontWeight: 500, color: 'var(--color-text-secondary)', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                >
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  Save draft
                </button>
                <button
                  type="button"
                  onClick={() => persist(true)}
                  disabled={saving || editingBlocks.length === 0}
                  data-testid="overlay-finalize"
                  className="cs-body inline-flex items-center gap-1 px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontWeight: 500, color: 'var(--color-on-brand)', background: 'var(--color-brand)' }}
                >
                  {saving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Lock className="w-3.5 h-3.5" />
                  )}
                  Finalize
                </button>
              </div>
            </div>

            {saveError && (
              <div
                className="cs-meta p-3 rounded-md"
                style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}
                data-testid="overlay-save-error"
              >
                {saveError}
              </div>
            )}
            {saveSuccess && (
              <div
                className="cs-meta p-3 rounded-md"
                style={{ background: 'transparent', border: '1px solid var(--color-verified)', color: 'var(--color-verified)' }}
                data-testid="overlay-save-success"
              >
                {saveSuccess}
              </div>
            )}
          </div>
        )}

        {/* Read-only viewer for finalized overlays */}
        {overlay && isFinalized && !loading && !error && (
          <div className="space-y-6">
            <div
              className="p-4"
              style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}
              data-testid="attorney-overlay-meta"
            >
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="cs-h3">
                    Version {overlay.version}
                  </h2>
                  <p className="cs-meta mt-0.5">
                    by {overlay.attorney_name} · last updated{' '}
                    {formatDate(overlay.updated_at)}
                  </p>
                </div>
                <span
                  className="cs-chip px-2 py-1 rounded flex items-center gap-1"
                  style={{ color: 'var(--color-text-muted)', background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)' }}
                  data-testid="attorney-overlay-status-finalized"
                >
                  <Lock className="w-3 h-3" />
                  finalized
                </span>
              </div>
              <div className="cs-meta mt-3 pt-3" style={{ borderTop: '1px solid var(--color-border-faint)' }}>
                Paired with calc run{' '}
                <code className="cs-figure px-1.5 py-0.5 rounded" style={{ background: 'var(--color-bg-elevated)' }}>
                  {overlay.attested_calc_ref}
                </code>
              </div>
            </div>

            <div
              data-testid="attorney-overlay-pdf-inclusion"
              className="cs-meta p-4 flex items-start gap-3"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 14, color: 'var(--color-text-secondary)' }}
            >
              <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
              <div>
                <p className="mb-1" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>PDF inclusion</p>
                <p>
                  Blocks marked <strong>final pdf</strong> render in a
                  dedicated <em>ATTORNEY WORK PRODUCT</em> section. Blocks
                  marked <strong>internal only</strong> stay off the
                  rendered PDF.
                </p>
                <p className="mt-2" data-testid="attorney-overlay-attestation-note">
                  If the report has been signed off by the in-house
                  forensic accountant, an <strong>ATTESTATION</strong> block
                  is also rendered above the overlay section, capturing the
                  accountant's name, sign-off date, and a court-admissible
                  attestation paragraph.
                </p>
              </div>
            </div>

            <div data-testid="attorney-overlay-blocks">
              <h3 className="cs-h3 mb-3">
                Content blocks ({overlay.content_blocks.length})
              </h3>
              {overlay.content_blocks.length === 0 ? (
                <p
                  className="cs-meta italic"
                  data-testid="attorney-overlay-blocks-empty"
                >
                  This overlay version has no content blocks.
                </p>
              ) : (
                <ul className="space-y-3">
                  {[...overlay.content_blocks]
                    .sort((a, b) => a.position - b.position)
                    .map((block) => (
                      <BlockRow key={block.block_id} block={block} />
                    ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
